import { z } from 'zod';

// #region GET /metadata/schema — 메타 입력 폼 정의 (data/metadataOptions.ts 목 대체)
export const metadataOptionSchema = z.object({
  code: z.string(),
  labelKo: z.string(),
  labelEn: z.string().nullish(),
});

/**
 * 필드 1개 정의 — inputType(select/input/date 등)·필수 여부·다중 선택·선택지.
 * multi=true면 값이 배열이다(요청·응답 모두 복수형 키 — 예: category → categories).
 */
export const metadataFieldSchema = z.object({
  field: z.string(),
  labelKo: z.string(),
  labelEn: z.string().nullish(),
  inputType: z.string(),
  required: z.boolean(),
  multi: z.boolean().nullish(),
  options: z.array(metadataOptionSchema),
});

/** MetadataSchemaResponse — 공통 필드 + 분류별 추가속성 정의 */
export const metadataSchemaResponseSchema = z.object({
  commonFields: z.array(metadataFieldSchema),
  /** key = 분류(category), value = 그 분류의 추가속성 필드들 */
  extraAttributesByCategory: z.record(z.array(metadataFieldSchema)),
});
export type MetadataOption = z.infer<typeof metadataOptionSchema>;
export type MetadataFieldSchema = z.infer<typeof metadataFieldSchema>;
export type MetadataSchemaResponse = z.infer<
  typeof metadataSchemaResponseSchema
>;
// #endregion

// #region /metadata/enums — select 선택지 관리 (R11 · 2026-08-24 swagger 반영)
/**
 * 드롭다운 값 1건. GET /metadata/schema가 주는 MetadataOption과 달리 **id**가 있어
 * 수정(PUT)·삭제(DELETE)가 가능하다.
 *
 * ⚠️ 목록 조회(GET /metadata/enums)는 스웨거에 아직 없다(2026-09-04 기준 405).
 * 설정 화면은 이 목록을 먼저 시도하고, 실패하면 schema의 options(= id 없음)로
 * 폴백해 **등록만** 가능한 모드로 내려간다 — 목록 API가 열리면 자동으로
 * 수정·삭제까지 동작한다.
 */
export const metadataEnumResponseSchema = z.object({
  id: z.number(),
  field: z.string(),
  category: z.string().nullish(),
  code: z.string(),
  labelKo: z.string().nullish(),
  labelEn: z.string().nullish(),
  sortOrder: z.number(),
  enabled: z.boolean(),
});
export type MetadataEnumResponse = z.infer<typeof metadataEnumResponseSchema>;

/** GET /metadata/enums — 필드 1개의 선택지 목록 */
export const metadataEnumListSchema = z.array(metadataEnumResponseSchema);

/** GET /metadata/enums 쿼리 파라미터 — 공통 필드는 category 생략 */
export type GetMetadataEnumsParams = {
  field: string;
  category?: string | null;
};

/** POST /metadata/enums — 신규 등록. field·code는 등록 시에만 정할 수 있다 */
export type CreateMetadataEnumBody = {
  field: string;
  /** 공통 필드는 null, 분류별 추가속성은 해당 분류명 */
  category?: string | null;
  code: string;
  labelKo?: string | null;
  labelEn?: string | null;
  sortOrder: number;
  enabled: boolean;
};

/** PUT /metadata/enums/{id} — 라벨·정렬·사용여부만 변경 가능(field·code 불변) */
export type UpdateMetadataEnumBody = {
  labelKo?: string | null;
  labelEn?: string | null;
  sortOrder: number;
  enabled: boolean;
};
// #endregion

// #region GET /metadata/role-holders — 메타데이터 role 보유자 (⚠️ 스웨거에 아직 없음 · 제안 계약)
/**
 * 메타데이터 설정 role(ADMIN · METADATA_MANAGER · METADATA_EDITOR)을 가진 사용자 1명.
 * 그룹 경유 부여도 사용자로 펼쳐서 내려주는 것을 전제로 한다. `roles`는 그 사용자가 가진
 * 메타데이터 role 목록 — 설정 화면은 여기서 삭제 가능자(ADMIN · METADATA_MANAGER)를 고른다.
 */
export const metadataRoleHolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullish(),
  leafGroupPath: z.string().nullish(),
  roles: z.array(z.string()),
});
export const metadataRoleHolderListSchema = z.array(metadataRoleHolderSchema);
export type MetadataRoleHolder = z.infer<typeof metadataRoleHolderSchema>;
// #endregion

// #region POST /metadata/validate — 저장 전 검증 (바디는 FileMetadataRequest)
export const metadataValidationErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
});
export const metadataValidationResultSchema = z.object({
  valid: z.boolean(),
  errors: z.array(metadataValidationErrorSchema),
});
export type MetadataValidationResult = z.infer<
  typeof metadataValidationResultSchema
>;
// #endregion
